<template>
    <form ref="form" autocomplete="off" @submit.prevent="formSubmit" class="contact-form">
        <div class="form-row">
            <div class="form-group">
                <input id="contact-name" v-model="formData.NAME" placeholder="Ваше имя" class="form-control" :class="{'has-error': errors.NAME}" maxlength="60" />
                <div v-if="errors.NAME" class="input-error-message">{{ errors.NAME }}</div>
            </div>
            <div class="form-group">
                <PhoneInput
                    v-model:modelValue="formData.TEL_NUMBER"
                    :error="errors.TEL_NUMBER"
                    label="Номер телефона"
                    @onValidPhone="handlePhoneValidation"
                />
                <div v-if="errors.TEL_NUMBER" class="input-error-message">{{ errors.TEL_NUMBER }}</div>
            </div>
        </div>
        <div class="form-row">
            <div class="form-group full-width">
                <input id="contact-email" v-model="formData.EMAIL" placeholder="Электронная почта" class="form-control" :class="{'has-error': errors.EMAIL}" />
                <div v-if="errors.EMAIL" class="input-error-message">{{ errors.EMAIL }}</div>
            </div>
        </div>
        <div class="form-row">
            <div class="form-group full-width">
                <textarea id="contact-comment" v-model="formData.COMMENT" maxlength="75" placeholder="Комментарий" class="form-control big-comment" :class="{'has-error': errors.COMMENT}"></textarea>
                <div v-if="errors.COMMENT" class="input-error-message">{{ errors.COMMENT }}</div>
            </div>
        </div>

        <div class="form-row">
            <div class="form-group checkbox-group" :class="{'has-error-checkbox': errors.AGREE}">
                <input type="checkbox" id="form-agree-checkbox" v-model="formData.AGREE" />
                <label for="form-agree-checkbox" class="checkbox-text">
                    <span>
                        Даю <a href="/upload/Согласие на обработку ПД (пользователь сайта)..docx" target="_blank" class="no-icon">согласие</a>
                        на обработку персональных данных, а также подтверждаю, что ознакомлен с
                        <a href="/upload/Политика обработки персональных данных..docx" target="_blank"  class="no-icon" rel="nofollow">Политикой обработки персональных данных</a>
                        </span>
                </label>
            </div>
        </div>

        <div class="form-row">
            <div class="form-group checkbox-group">
                <input type="checkbox" id="contact-subscribe" v-model="formData.SUBSCRIBE" />
                <label for="contact-subscribe" class="checkbox-text">
                    <span>
                        Даю <a href="/upload/Согласие на получение рекламной рассылки..docx" target="_blank" class="no-icon">согласие</a>
                        на получение рекламных рассылок на указанный адрес электронной почты
                    </span>
                </label>
            </div>
        </div>

        <div class="form-row">
            <button type="submit" class="submit-button" :disabled="isSubmitting || pending || (!props.isModal && !formData.AGREE)">
                <span v-if="isSubmitting || pending">Отправка...</span>
                <span v-else>Отправить заявку</span>
            </button>
        </div>
    </form>
</template>

<script setup>
import { ref, reactive, defineProps, defineEmits } from 'vue';
import { useContentPages } from '@/stores/homeStores';
import { sendContact } from '@/api/sendContactForm/sendContact';
import PhoneInput from '@/components/Input/PhoneInput.vue';

const props = defineProps({
    isModal: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['close', 'submit-success', 'submit-error']);

const formData = reactive({
    NAME: '',
    TEL_NUMBER: '',
    EMAIL: '',
    COMMENT: '',
    AGREE: false,
    SUBSCRIBE: false,
});

const errors = reactive({
    NAME: '',
    TEL_NUMBER: '',
    EMAIL: '',
    COMMENT: '',
    AGREE: ''
});

const isPhoneValid = ref(false);

const handlePhoneValidation = (isValid) => {
    isPhoneValid.value = isValid;
    if (isValid) {
        errors.TEL_NUMBER = '';
    }
};

const isSubmitting = ref(false);
const pending = ref(false);
const store = useContentPages();

const successMessage = 'Наш менеджер скоро вам перезвонит для уточнения деталей.';
const webFormName = 'FEEDBACK_EXPRESS';
const originalFields = {
    NAME: { required: true, message: 'Пожалуйста, введите ваше имя.' },
    TEL_NUMBER: { required: true, message: 'Пожалуйста, введите ваш номер телефона.' },
    EMAIL: { required: true, message: 'Пожалуйста, введите ваш E-mail.' },
    AGREE: { required: true, message: 'Необходимо согласие на обработку персональных данных.' },
};

function flushForm() {
    Object.assign(formData, { NAME: '', TEL_NUMBER: '', EMAIL: '', COMMENT: '', AGREE: false, SUBSCRIBE: false });
    Object.keys(errors).forEach(key => errors[key] = '');
}

function clearErrors() {
     Object.keys(errors).forEach(key => errors[key] = '');
}

const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

async function formSubmit() {
    clearErrors();

    if (props.isModal) {
        let hasError = false;
        if (!formData.NAME) {
            errors.NAME = 'Пожалуйста, введите ваше имя.';
            hasError = true;
        } else if (!/^[a-zA-Zа-яА-Я\s]*$/.test(formData.NAME)) {
            errors.NAME = 'Имя может содержать только буквы.';
            hasError = true;
        }

        if (!formData.TEL_NUMBER && !formData.EMAIL) {
            errors.TEL_NUMBER = 'Необходимо указать телефон или электронную почту';
            errors.EMAIL = 'Необходимо указать телефон или электронную почту';
            hasError = true;
        }
        if (formData.EMAIL && !validateEmail(formData.EMAIL)) {
            errors.EMAIL = 'Пожалуйста, введите корректный адрес электронной почты.';
            hasError = true;
        }
        if (formData.TEL_NUMBER && !isPhoneValid.value) {
            errors.TEL_NUMBER = 'Пожалуйста, введите корректный номер телефона.';
            hasError = true;
        }
        if (!formData.AGREE) {
            errors.AGREE = 'Необходимо согласие на обработку персональных данных.';
            hasError = true;
        }

        if (hasError) return;

        isSubmitting.value = true;
        try {
            if (window.dataLayer) {
                window.dataLayer.push({'event': 'application_by'});
            }
            const calculatorData = store.getContactFormData;
            const submissionData = {
                name: formData.NAME,
                tel_number: formData.TEL_NUMBER,
                email: formData.EMAIL,
                comment: formData.COMMENT,
                privacy_policy: formData.AGREE,
                newsletter: formData.SUBSCRIBE,
                calculator: calculatorData,
                bitrix_privacy_consent: formData.AGREE,
                bitrix_newsletter_consent: formData.SUBSCRIBE,
                email_to: store.getContactFormData?.email || null,
            };
            await sendContact(submissionData);
            emit('submit-success');
            flushForm();
        } catch (error) {
            console.error('Form submission error:', error);
            const genericErrorMessage = 'Произошла ошибка при отправке. Попробуйте позже.';
            let errorMessage = genericErrorMessage;

            if (error.response && error.response.data && error.response.data.errors) {
                const serverErrors = error.response.data.errors;
                Object.keys(serverErrors).forEach(key => {
                    if (errors.hasOwnProperty(key.toUpperCase())) {
                        errors[key.toUpperCase()] = serverErrors[key][0];
                    }
                });
                errorMessage = error.response.data.message || genericErrorMessage;
            }

            store.setErrorMessage(errorMessage);
            store.updateErrorModal(true);
            emit('submit-error');
        } finally {
            isSubmitting.value = false;
        }
    } else {
        let hasErrorOriginal = false;
        Object.keys(originalFields).forEach((fieldName) => {
            const value = formData[fieldName];
            if (originalFields[fieldName].required && !value) {
                errors[fieldName] = originalFields[fieldName].message;
                hasErrorOriginal = true;
            } else if (value) {
                switch (fieldName) {
                    case 'NAME':
                        if (!/^[a-zA-Zа-яА-Я\s]*$/.test(value)) {
                            errors.NAME = 'Имя может содержать только буквы.';
                            hasErrorOriginal = true;
                        }
                        break;
                    case 'TEL_NUMBER':
                        if (!isPhoneValid.value) {
                            errors.TEL_NUMBER = 'Некорректный формат телефона';
                            hasErrorOriginal = true;
                        }
                        break;
                    case 'EMAIL':
                        if (!validateEmail(value)) {
                            errors.EMAIL = 'Некорректный формат электронной почты';
                            hasErrorOriginal = true;
                        }
                        break;
                }
            }
        });

        if (hasErrorOriginal) return;

        pending.value = true;
        try {
            if (window.dataLayer) {
                window.dataLayer.push({'event': 'application_by'});
            }
            const submitData = {
                ...{ TAGS: 'express', WEB_FORM_CODE: webFormName },
                name: formData.NAME,
                tel_number: formData.TEL_NUMBER,
                email: formData.EMAIL,
                comment: formData.COMMENT,
                AGREE: formData.AGREE ? 'Y' : 'N',
                SUBSCRIBE: formData.SUBSCRIBE ? 'Y' : 'N',
                email_to: store.getContactFormData?.email || null,
            };

            await sendContact(submitData);

            store.setSuccessMessage(successMessage);
            store.updateSuccessModal(true);
            flushForm();
        } catch (error) {
            console.error('Form submission error:', error);
            const genericErrorMessage = 'Произошла ошибка при отправке. Попробуйте позже.';
            let errorMessage = genericErrorMessage;

            if (error.response && error.response.data && error.response.data.errors) {
                const serverErrors = error.response.data.errors;
                Object.keys(serverErrors).forEach(key => {
                    const upperKey = key.toUpperCase();
                    if (errors.hasOwnProperty(upperKey)) {
                        errors[upperKey] = serverErrors[key][0];
                    }
                });
                errorMessage = error.response.data.message || genericErrorMessage;
            }

            store.setErrorMessage(errorMessage);
            store.updateErrorModal(true);
            emit('submit-error');
        } finally {
            pending.value = false;
        }
    }
}
</script>

<style scoped>
.form-row {
    display: flex;
    gap: 20px;
}
.form-group {
    flex: 1;
    display: flex;
    flex-direction: column;
    position: relative;
}
.form-group.full-width {
    flex-basis: 100%;
}
.form-control {
    width: 100%;
    padding: 16px;
    border: 1px solid #E4E6E7;
    border-radius: 6px;
    font-size: 16px;
    box-sizing: border-box;
}
.form-control.has-error {
    border-color: #dc3545;
}
.input-error-message {
    color: #dc3545;
    margin-top: 4px;
    font-weight: 400;
    font-style: Regular;
    font-size: 12px;
    leading-trim: NONE;
    line-height: 18px;
    letter-spacing: 0%;

}
.form-control.big-comment {
    height: 116px;
    resize: vertical;
}
.checkbox-group {
    flex-direction: row;
    align-items: center;
    font-size: 14px;
    color: #757575;
}
.checkbox-group.has-error-checkbox label {
    color: #dc3545;
}
.checkbox-group input {
    margin-right: 10px;
}
.checkbox-text {
    font-weight: 400;
    font-style: Regular;
    font-size: 14px;
    leading-trim: NONE;
    line-height: 20px;
    letter-spacing: 0%;
    margin: 0;
}
.submit-button {
    padding: 16px 28px;
    color: #FFFFFF;
    background-color: #E4003C;
    border-radius: 6px;
    border: none;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    transition: background-color 0.3s;
}
.submit-button:disabled {
    background-color: #AAAAAA;
    cursor: not-allowed;
}
.no-icon {
    color: #48538B;
    text-decoration: underline;
    font-weight: 700;
    font-style: Bold;
    font-size: 14px;
    leading-trim: NONE;
    line-height: 20px;
    letter-spacing: 0%;
    margin: 0;
}
.no-icon:hover {
    color: #e4003c;
}

@media (max-width: 767px) {
    .form-row {
        flex-direction: column;
        gap: 16px;
    }
    .form-group {
        gap: 4px;
    }
}
</style>
